"use client";

import { useState } from "react";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

type ExplorerLanguage = "en" | "hi";

interface MahadashaMeta {
  lord: string;
  lord_hi: string;
  years: number;
  ruler: string;
  element: string;
  theme: string;
  upay: string;
}

/* Hindi reading of the same evergreen reference rows. Rahu and Ketu carry no
   tattva of their own; the classical "shanivat Rahu, kujavat Ketu" rule gives
   them Saturn's air and Mars's fire, which is what the English rows paraphrase. */
const MAHADASHA_HI: Record<string, Pick<MahadashaMeta, "ruler" | "element" | "theme" | "upay">> = {
  Ketu: {
    ruler: "छाया ग्रह (दक्षिण पात)",
    element: "अग्नि (मंगलवत्)",
    theme: "आध्यात्मिक वैराग्य, भीतरी जागृति, पुराने कर्मों का निपटारा और जीवन में अचानक बदलाव।",
    upay: "गणेश पूजा, ध्यान और गली के कुत्तों को भोजन कराना।",
  },
  Venus: {
    ruler: "शुक्र",
    element: "जल",
    theme: "प्रेम, विवाह, कला, सुख-सुविधा, वाहन, आर्थिक समृद्धि और आराम।",
    upay: "लक्ष्मी उपासना, स्वच्छ सफ़ेद वस्त्र पहनना और स्त्रियों के प्रति सम्मानपूर्ण व्यवहार।",
  },
  Sun: {
    ruler: "सूर्य",
    element: "अग्नि",
    theme: "अधिकार, आत्म-बोध, नेतृत्व, शासन से जुड़े संबंध और पिता से मिली विरासत।",
    upay: "सूर्योदय के समय सूर्य को अर्घ्य, गायत्री मंत्र का जप और पिता व बड़ों का सम्मान।",
  },
  Moon: {
    ruler: "चंद्र",
    element: "जल",
    theme: "मन की शांति, भावनात्मक विकास, माता का आशीर्वाद, अंतर्ज्ञान और जनसंपर्क।",
    upay: "सोमवार को शिव अभिषेक, चांदी के गिलास में पानी पीना और माता की सेवा।",
  },
  Mars: {
    ruler: "मंगल",
    element: "अग्नि",
    theme: "साहस, शारीरिक शक्ति, भूमि-भवन की प्राप्ति, नेतृत्व और साहसिक पहल।",
    upay: "मंगलवार को हनुमान चालीसा का पाठ और रक्तदान या मसूर दाल का दान।",
  },
  Rahu: {
    ruler: "छाया ग्रह (उत्तर पात)",
    element: "वायु (शनिवत्)",
    theme:
      "सांसारिक महत्वाकांक्षा, विदेश के अवसर, लीक से हटकर सफलता, नवाचार और मन की तीव्र प्रेरणा।",
    upay: "भैरव उपासना, पक्षियों को दाना डालना और बिना ठोस आधार की सट्टेबाज़ी से बचना।",
  },
  Jupiter: {
    ruler: "बृहस्पति",
    element: "आकाश",
    theme: "उच्च ज्ञान, धन, संतान, धर्मसम्मत विस्तार, आध्यात्मिक मार्गदर्शन और शिक्षा में सफलता।",
    upay: "गुरुजनों का सम्मान, केसर का तिलक और गुरुवार को चने की दाल का दान।",
  },
  Saturn: {
    ruler: "शनि",
    element: "वायु",
    theme: "अनुशासन, धैर्य, करियर की दीर्घकालिक नींव, कर्मों का न्याय और परिपक्व सहनशक्ति।",
    upay: "शनि चालीसा, पीपल के नीचे सरसों के तेल का दीपक और श्रमिकों की सहायता।",
  },
  Mercury: {
    ruler: "बुध",
    element: "पृथ्वी",
    theme: "व्यापार, विश्लेषण की क्षमता, संवाद, लेखन, लेन-देन और अध्ययन।",
    upay: "बुधवार को गाय को हरा चारा खिलाना और विष्णु सहस्रनाम का पाठ।",
  },
};

const EXPLORER_COPY = {
  en: {
    eyebrow: "Interactive Vedic Explorer",
    title: "120-Year Vimshottari Mahadasha & Antardasha Calculator (दशा चक्र)",
    intro:
      "Click on any planet to explore its Mahadasha duration, life theme, and mathematical Antardasha sub-period breakdown.",
    yrsShort: (n: number) => `${n} yrs`,
    mahadasha: (m: MahadashaMeta) => `${m.lord} (${m.lord_hi}) Mahadasha`,
    total: (years: number, pct: number) => `${years} Years Total (${pct}% of Life)`,
    ruler: "Ruler:",
    element: "Element:",
    consult: (m: MahadashaMeta) => `Consult on ${m.lord} Dasha ✦`,
    themes: "Core Life Themes & Influence",
    upay: "Classical Upay & Harmonization",
    antarTitle: (m: MahadashaMeta) => `9 Antardasha (Sub-Period) Durations in ${m.lord} Mahadasha`,
  },
  hi: {
    eyebrow: "इंटरैक्टिव दशा चक्र",
    title: "120 वर्ष का विंशोत्तरी महादशा और अंतर्दशा चक्र",
    intro:
      "किसी भी ग्रह को चुनें और उसकी महादशा की अवधि, जीवन के मुख्य विषय और नौ अंतर्दशाओं की अवधि देखें।",
    yrsShort: (n: number) => `${n} वर्ष`,
    mahadasha: (m: MahadashaMeta) => `${m.lord_hi} महादशा`,
    total: (years: number, pct: number) => `कुल ${years} वर्ष (120 वर्ष के चक्र का ${pct}%)`,
    ruler: "स्वामी:",
    element: "तत्व:",
    consult: (m: MahadashaMeta) => `${m.lord_hi} दशा पर प्रश्न पूछें ✦`,
    themes: "जीवन के मुख्य विषय",
    upay: "पारंपरिक उपाय",
    antarTitle: (m: MahadashaMeta) => `${m.lord_hi} महादशा में 9 अंतर्दशाओं की अवधि`,
  },
} as const;

function localizeMaha(maha: MahadashaMeta, language: ExplorerLanguage): MahadashaMeta {
  return language === "hi" ? { ...maha, ...MAHADASHA_HI[maha.lord] } : maha;
}

function durationLabel(
  length: { years: number; months: number; days: number },
  language: ExplorerLanguage
): string {
  if (language === "hi") {
    return [
      length.years > 0 ? `${length.years} वर्ष` : "",
      length.months > 0 ? `${length.months} माह` : "",
      length.days > 0 && length.years === 0 ? `${length.days} दिन` : "",
    ]
      .filter(Boolean)
      .join(" ");
  }
  return [
    length.years > 0 ? `${length.years} yr${length.years > 1 ? "s" : ""}` : "",
    length.months > 0 ? `${length.months} mo${length.months > 1 ? "s" : ""}` : "",
    length.days > 0 && length.years === 0 ? `${length.days} d` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

const MAHADASHA_CYCLE: MahadashaMeta[] = [
  {
    lord: "Ketu",
    lord_hi: "केतु",
    years: 7,
    ruler: "Shadow Planet (South Node)",
    element: "Spiritual Fire",
    theme: "Spiritual detachment, inner awakening, past karma resolution, and sudden life shifts.",
    upay: "Ganesh worship, meditation, and feeding street dogs.",
  },
  {
    lord: "Venus",
    lord_hi: "शुक्र",
    years: 20,
    ruler: "Shukra",
    element: "Water",
    theme:
      "Love, marriage, artistic pursuits, luxury, vehicles, financial prosperity, and comfort.",
    upay: "Lakshmi upasana, wearing clean white clothes, and respectful conduct with women.",
  },
  {
    lord: "Sun",
    lord_hi: "सूर्य",
    years: 6,
    ruler: "Surya",
    element: "Fire",
    theme: "Authority, self-realization, leadership, government connections, and fatherly legacy.",
    upay: "Surya Arghya at sunrise, reciting Gayatri Mantra, and honoring father and elders.",
  },
  {
    lord: "Moon",
    lord_hi: "चंद्र",
    years: 10,
    ruler: "Chandra",
    element: "Water",
    theme:
      "Mental peace, emotional growth, mother's blessings, intuition, and public relationships.",
    upay: "Shiva Abhishek on Mondays, drinking water from silver glass, and caring for mother.",
  },
  {
    lord: "Mars",
    lord_hi: "मंगल",
    years: 7,
    ruler: "Mangal",
    element: "Fire",
    theme: "Courage, physical stamina, real estate acquisitions, leadership, and bold initiatives.",
    upay: "Hanuman Chalisa recitation on Tuesdays and donating blood or red lentils.",
  },
  {
    lord: "Rahu",
    lord_hi: "राहु",
    years: 18,
    ruler: "Shadow Planet (North Node)",
    element: "Air & Smoke",
    theme:
      "Worldly ambition, foreign opportunities, unconventional success, innovation, and intense mental drive.",
    upay: "Bhairav worship, feeding birds, and avoiding ungrounded speculation.",
  },
  {
    lord: "Jupiter",
    lord_hi: "गुरु",
    years: 16,
    ruler: "Brihaspati",
    element: "Ether",
    theme:
      "Higher wisdom, wealth, childbirth, righteous expansion, spiritual mentorship, and academic success.",
    upay: "Honoring teachers and gurus, applying saffron tilak, and offering chana dal on Thursdays.",
  },
  {
    lord: "Saturn",
    lord_hi: "शनि",
    years: 19,
    ruler: "Shani",
    element: "Air",
    theme:
      "Discipline, perseverance, long-term career foundations, karmic justice, and mature endurance.",
    upay: "Shani Chalisa, lighting mustard oil lamp under Peepal tree, and helping laborers.",
  },
  {
    lord: "Mercury",
    lord_hi: "बुध",
    years: 17,
    ruler: "Budh",
    element: "Earth",
    theme: "Business commerce, analytical intellect, communication, writing, trade, and learning.",
    upay: "Feeding green fodder to cows on Wednesdays and Vishnu Sahasranama chanting.",
  },
];

function calculateAntardashaLength(
  mahaYears: number,
  antarYears: number
): { years: number; months: number; days: number } {
  const totalMonths = (mahaYears * antarYears * 12) / 120;
  const y = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  const m = Math.floor(remainingMonths);
  const d = Math.round((remainingMonths - m) * 30);
  return { years: y, months: m, days: d };
}

export default function DashaExplorer({ language = "en" }: { language?: ExplorerLanguage }) {
  const [selectedLord, setSelectedLord] = useState<string>("Jupiter");

  const currentMaha = localizeMaha(
    MAHADASHA_CYCLE.find((m) => m.lord === selectedLord) || MAHADASHA_CYCLE[6],
    language
  );
  const startIdx = MAHADASHA_CYCLE.findIndex((m) => m.lord === selectedLord);
  const orderedAntardashas = [
    ...MAHADASHA_CYCLE.slice(startIdx),
    ...MAHADASHA_CYCLE.slice(0, startIdx),
  ];

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/dasha-calculator"),
    "/dasha-calculator",
    language
  );

  const handleConsult = (antarLord: string) => {
    const prompt = `I want to understand the impact of ${currentMaha.lord} Mahadasha with ${antarLord} Antardasha in Vedic astrology. What should I expect and what are the best remedies?`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/dasha-calculator");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-6 sm:p-8 shadow-sm">
      <DashaExplorerHeader language={language} />
      <DashaTimelineBar
        selectedLord={selectedLord}
        onSelect={setSelectedLord}
        language={language}
      />
      <MahadashaDetailCard
        currentMaha={currentMaha}
        chatHref={chatHref}
        onConsult={() => handleConsult(currentMaha.lord)}
        language={language}
      />
      <AntardashaGrid
        currentMaha={currentMaha}
        orderedAntardashas={orderedAntardashas}
        language={language}
      />
    </div>
  );
}

function DashaExplorerHeader({ language }: { language: ExplorerLanguage }) {
  const copy = EXPLORER_COPY[language];
  return (
    <div className="text-center max-w-2xl mx-auto mb-8">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        {copy.eyebrow}
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">{copy.title}</h2>
      <p className="text-body-small text-text-secondary mt-2">{copy.intro}</p>
    </div>
  );
}

function DashaTimelineBar({
  selectedLord,
  onSelect,
  language,
}: {
  selectedLord: string;
  onSelect: (lord: string) => void;
  language: ExplorerLanguage;
}) {
  const hi = language === "hi";
  return (
    <div className="overflow-x-auto pb-2 mb-6">
      <div className="flex gap-2 min-w-[700px]">
        {MAHADASHA_CYCLE.map((maha) => {
          const isSelected = maha.lord === selectedLord;
          return (
            <button
              key={maha.lord}
              type="button"
              onClick={() => onSelect(maha.lord)}
              className={`flex-1 min-w-[72px] py-3 px-2 rounded-xl text-center border transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm"
              }`}
            >
              <span
                className={`block text-body-small font-bold ${isSelected ? "text-intent-brand" : "text-text-primary"}`}
              >
                {hi ? maha.lord_hi : maha.lord}
              </span>
              {hi ? null : (
                <span className="block text-caption font-medium text-text-muted mt-0.5">
                  {maha.lord_hi}
                </span>
              )}
              <span className="inline-block mt-1 text-eyebrow font-bold px-2 py-0.5 rounded-full bg-surface-raised text-text-secondary">
                {EXPLORER_COPY[language].yrsShort(maha.years)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MahadashaDetailCard({
  currentMaha,
  chatHref,
  onConsult,
  language,
}: {
  currentMaha: MahadashaMeta;
  chatHref: string;
  onConsult: () => void;
  language: ExplorerLanguage;
}) {
  const copy = EXPLORER_COPY[language];
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {copy.mahadasha(currentMaha)}
            </h3>
            <span className="px-3 py-1 rounded-full text-caption font-bold bg-surface-brand-subtle text-intent-brand">
              {copy.total(currentMaha.years, Math.round((currentMaha.years / 120) * 100))}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            {copy.ruler} <strong className="text-text-primary">{currentMaha.ruler}</strong> ·{" "}
            {copy.element} <strong className="text-text-primary">{currentMaha.element}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-4 py-2 text-caption font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors"
        >
          {copy.consult(currentMaha)}
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div>
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted">
            {copy.themes}
          </p>
          <p className="text-body-small text-text-primary leading-relaxed mt-1">
            {currentMaha.theme}
          </p>
        </div>
        <div>
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted">
            {copy.upay}
          </p>
          <p className="text-body-small text-text-primary leading-relaxed mt-1">
            {currentMaha.upay}
          </p>
        </div>
      </div>
    </div>
  );
}

function AntardashaGrid({
  currentMaha,
  orderedAntardashas,
  language,
}: {
  currentMaha: MahadashaMeta;
  orderedAntardashas: MahadashaMeta[];
  language: ExplorerLanguage;
}) {
  const hi = language === "hi";
  return (
    <div>
      <h4 className="text-body-small font-bold uppercase tracking-wider text-text-primary mb-3">
        {EXPLORER_COPY[language].antarTitle(currentMaha)}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {orderedAntardashas.map((antar) => {
          const length = calculateAntardashaLength(currentMaha.years, antar.years);
          const durationText = durationLabel(length, language);

          return (
            <div
              key={antar.lord}
              className="p-3.5 rounded-xl border border-border-default bg-surface-raised flex items-center justify-between"
            >
              <div>
                <span className="text-body-small font-bold text-text-primary">
                  {hi
                    ? `${currentMaha.lord_hi}–${antar.lord_hi}`
                    : `${currentMaha.lord}–${antar.lord}`}
                </span>
                {hi ? null : (
                  <span className="block text-caption text-text-muted mt-0.5">
                    {currentMaha.lord_hi}–{antar.lord_hi}
                  </span>
                )}
              </div>
              <div className="text-right">
                <span className="inline-block px-2.5 py-1 rounded-md text-caption font-bold bg-surface-raised text-text-primary border border-border-default">
                  {durationText || (hi ? "18 दिन" : "18 days")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
